/* eslint-disable camelcase */
/* eslint-disable max-len */
import React, {ReactElement, useEffect, useState} from 'react';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  Select,
  MenuItem,
  Switch,
  Link
} from '@material-ui/core';

import NavRoutes from '../../../constants/route/NavRoutes';
import {SnackBarConfig} from '../../../utils/SnackBarConfig';
import {
  APP,
  FONT_SECONDARY_COLOR,
  BORDER_PRIMARY_COLOR,
  STATE_OBJECT_BORDER,
  STATE_OBJECT_OPACITY
} from 'src/style/variable';
import './Register.scss';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import PhoneInputField from '@components/system/PhoneInputField/PhoneInputField';
import CustomSelectField from 'src/modules/admin/modules/ClinicGroups/AddUser/CustomSelectField';
import {addGroupValidationWithHookForm} from '@shared/modules/clinics/Clinics';
import {addClinicGroupPublic} from '@shared/services/ClinicServices';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import validateZipCode from '@shared/utils/zipCode';
import {referenceDataService} from '@shared/services/ReferenceData.service';
import LogoIcon from '../login/LogoIcon';
interface CountryData {
  name: string;
  dialCode: string;
  countryCode: string;
  format: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    FormLabel: {
      left: '-0.8rem',
      fontSize: '1.2rem',
      color: FONT_SECONDARY_COLOR
    },
    InstructionLabel: {
      fontSize: '1.5rem'
    },
    appBar: {
      color: theme.palette.text.primary,
      background: theme.palette.background.paper,
      boxShadow: 'none',
      [theme.breakpoints.up('md')]: {
        width: `100%`
      }
    },
    formWrapper: {
      borderRadius: 0,
      marginTop: '0.7rem !important',
      '& .MuiOutlinedInput-notchedOutline ': {
        borderColor: BORDER_PRIMARY_COLOR,
        top: 0,
        '& legend': {
          display: 'none'
        }
      }
    },
    button: {
      fontFamily: APP.FONT_FAMILY.BOLD
    },
    title: {
      display: 'flex',
      left: 0,
      paddingBottom: '2.5rem',
      fontFamily: APP.FONT_FAMILY.MEDIUM,
      fontSize: '1.7rem'
    },
    registerButton: {
      justifyContent: 'center',
      display: 'flex',
      textAlign: 'center',
      alignItems: 'center'
    },
    copyRightText: {
      fontSize: '1rem'
    },
    policy: {
      paddingBottom: '1 rem !important',
      marginBottom: '1 rem !important'
    },
    checkbox: {
      textAlign: 'left',
      height: '2.5rem',
      '& .MuiFormControlLabel-label': {
        fontFamily: APP.FONT_FAMILY.BOLD,
        fontSize: '1rem'
      },
      '& .MuiSvgIcon-root': {
        fontSize: '2rem'
      }
    },
    registerWrapper: {
      '& .MuiFormControl-root': {
        paddingBottom: '1rem !important'
      }
    },
    privacyPolicy: {
      marginTop: '1rem',
      '& .MuiFormControl-root': {
        paddingBottom: '0 !important'
      }
    },
    termsAndPolicy: {
      marginTop: '1rem',
      border: `1px solid ${BORDER_PRIMARY_COLOR}`,
      backgroundColor: '#E7E8EA',
      maxHeight: '14.5rem',
      padding: '0.9rem',
      overflowY: 'scroll',
      textAlign: 'left',
      whiteSpace: 'pre-line',
      fontFamily: APP.FONT_FAMILY.MEDIUM,
      fontSize: '0.8rem'
    },
    input: {
      '& .MuiDialog-root': {},
      '& .MuiDialogTitle-root': {
        // marginRight:'0.3rem'
      },
      '& .MuiSelect-nativeInput': {
        border: 'none',
        height: '2rem',
        paddingRight: '2rem'
      },
      width: '100%',
      borderRadius: '4px'
    },
    logo: {
      '@media (min-width: 0px) and (max-width: 1023px) and (min-height: 500px)': {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: '40px'
      },
      '@media (min-height: 0px) and (max-height: 499px)': {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: '0px'
      },
      '@media (min-width: 1024px)': {
        display: 'none'
      }
    },
    login: {
      display: 'flex',
      marginTop: '25px',
      // width: '-webkit-fill-available',
      width: '100%',
      justifyContent: 'center',
      '@media (min-height: 0px) and (max-height: 499px)': {
        marginTop: '5px'
      }
    },
    addressText: {
      color: '#777777',
      fontSize: '1.5rem',
      paddingBottom: '1rem',
      paddingTop: '1rem'
    },
    dividerView: {
      borderTop: '#c1c1c1 solid 1px',
      paddingBottom: '1.3rem'
    }
  })
);

// const GreenCheckbox = withStyles({
//   root: {
//     color: FONT_TERTIARY_COLOR,
//     '&$checked': {
//       color: APP_SECONDARY_COLOR
//     }
//   },
//   checked: {}
// })(props => <Checkbox color="default" {...props} />);

const Register = (props: any): ReactElement => {
  const {history} = props;
  const classes = useStyles();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [state, setState]: any = useState(t('FORM.PLACEHOLDERS.STATE'));
  const [buttonsDisable, setButtonsDisable] = useState<boolean>(false);
  const [EMRProvider, setEMRProvider]: any = useState({label: '', value: null});
  const [sharedFlag, updateSharedFlag] = useState<boolean>(false);
  const [statesList, setStatesList]: any = useState([]);
  const [EMRProvidersList, setEMRProvidersList]: any = useState([]);
  const {control, handleSubmit, errors, setValue} = useForm({
    mode: 'all',
    resolver: async (values: any) => {
      values.state = state;
      if (values.state === t('FORM.PLACEHOLDERS.STATE')) {
        values.state = '';
      }
      return {
        values,
        errors: addGroupValidationWithHookForm(values)
      };
    }
  });

  const redirectToLogin = () => {
    history.push(`/${NavRoutes.PUBLIC.LOGIN}`);
  };

  const stateWithOpacity = {
    border: `1px solid ${STATE_OBJECT_BORDER}`,
    height: '2.3rem',
    opacity: STATE_OBJECT_OPACITY
  };

  const stateStyles = {border: '1px solid #c1c1c1', height: '2.3rem'};

  const stateErrorStyles = {
    border: '1px solid red',
    height: '2.3rem',
    opacity: 0.5
  };

  const getReferenceStates = (): void => {
    referenceDataService({key: 'emr-provider'})
      .then(res => {
        if (res && res?.data && res?.data?.rows) {
          setEMRProvidersList(res?.data?.rows);
        }
      })
      .catch(err => {
        enqueueSnackbar(
          (err && err.message) || t('ADD_CLINIC.REFERENCE_STATE_ERROR'),
          SnackBarConfig('e')
        );
        throw err;
      });
    referenceDataService({key: 'ref-states'})
      .then(res => {
        if (res && res?.data && res?.data?.rows) {
          setStatesList(res?.data?.rows);
        }
      })
      .catch(err => {
        enqueueSnackbar(
          (err && err.message) || t('ADD_CLINIC.REFERENCE_STATE_ERROR'),
          SnackBarConfig('e')
        );
        throw err;
      });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateSharedFlag(event.target.checked);
    // setState({isloggedIn: true});
  };

  const handleOnChange = (event: any, country: CountryData, key: any): any => {
    event.persist();
    event.target.setAttribute('autocomplete', 'off');
    setTimeout(() => {
      setValue(key, '' + event?.target?.value, {
        shouldValidate: true
      });
    }, 10);
  };

  const handleStateChange = (event: React.ChangeEvent<{value: unknown}>) => {
    setState(event.target.value);
    errors.state = false;
  };

  const handleEMRProvider = (value: number, label: string) => {
    setEMRProvider({
      label: label,
      value: value
    });
  };
  const handleFormSubmit = (values: any): void => {
    setButtonsDisable(true);
    values.country = 'us';
    values.emrprovider = EMRProvider.value;
    values.patients_shared = sharedFlag;
    addClinicGroupPublic(values)
      .then(res => {
        if (res) {
          enqueueSnackbar(
            t('ADD_CLINIC.ADD_CLINIC_GROUP_SUCCESS'),
            SnackBarConfig('s')
          );
          redirectToLogin();
        }
      })
      .catch(err => {
        enqueueSnackbar(
          (err && err.message) || t('ADD_CLINIC.ADD_CLINIC_GROUP_ERROR'),
          SnackBarConfig('e')
        );
        throw err;
      })
      .finally(() => {
        setButtonsDisable(false);
      });
  };

  const checkFormState = (): void => {
    if (Object.keys(errors).length > 0) {
      enqueueSnackbar(
        t('VALIDATORS.REQUIRED_FORM_FIELD_MESSAGE'),
        SnackBarConfig('w')
      );
    }
  };

  const renderInstructions = () => {
    return (
      <div style={{marginLeft: '-1.5rem'}}>
        <ol>
          <li> {t('REGISTER.INSTRUCTIONS.FIRST')} </li>
          <li> {t('REGISTER.INSTRUCTIONS.SECOND')} </li>
          <li> {t('REGISTER.INSTRUCTIONS.THIRD')} </li>
          <li> {t('REGISTER.INSTRUCTIONS.FOURTH')} </li>
        </ol>
      </div>
    );
  };

  useEffect(() => {
    getReferenceStates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="register-wrapper center-children">
      <LogoIcon />
      <div className="register-content-wrap">
        {/* New  */}

        <div className="register-form-content">
          <div className="register-form">
            {/* New  */}
            <Typography className={classes.title} variant={'h3'}>
              {t('REGISTER.TITLE')}
            </Typography>
            <div>
              <InputLabel
                shrink
                className={[classes.FormLabel, classes.InstructionLabel].join(
                  ' '
                )}>
                {t('REGISTER.INSTRUCTIONS.INSTRUCTION')}
              </InputLabel>
              {renderInstructions()}
            </div>

            <div className={classes.addressText}>
              {t('ADD_CLINIC.GROUP_DETAILS')}
            </div>
            <div className={classes.dividerView} />
            <div>{t('ADD_CLINIC.CLINIC_GROUP_NAME')}*</div>
            <FormControl
              error={!!errors.clinic_group_name}
              fullWidth={true}
              variant="outlined">
              <Controller
                id="clinic_group_name"
                as={StyledOutlinedInput}
                margin={'dense'}
                control={control}
                name={'clinic_group_name'}
                type={'text'}
                value={'text'}
                autoComplete="on"
                placeholder={'Add clinic group name'}
                inputProps={{maxLength: '100'}}
                defaultValue={''}
                error={!!errors.clinic_group_name}
              />
              {errors.clinic_group_name && (
                <FormHelperText>{t(errors.clinic_group_name)}</FormHelperText>
              )}
            </FormControl>
            <div>{t('ADD_CLINIC.PRIMARY_CONTACT_NAME')}</div>
            <FormControl fullWidth={true} variant="outlined">
              <Controller
                id="primary_contact_name"
                as={StyledOutlinedInput}
                margin={'dense'}
                control={control}
                name={'primary_contact_name'}
                type={'text'}
                value={'text'}
                autoComplete="on"
                placeholder={'Add Primary Contact Name'}
                defaultValue={''}
              />
            </FormControl>
            <div>{t('ADD_CLINIC.PRIMARY_CONTACT_PHONE')}</div>
            <FormControl fullWidth={true} variant="outlined">
              <Controller
                id="primary_contact_phone"
                as={
                  <PhoneInputField
                    value={''}
                    inputProps={{
                      name: 'primary_contact_phone',
                      id: 'primary_contact_phone',
                      required: false
                    }}
                    onFocus={(value: any, country: CountryData) =>
                      handleOnChange(value, country, 'primary_contact_phone')
                    }
                  />
                }
                margin={'dense'}
                control={control}
                name={'primary_contact_phone'}
                type={'number'}
                value={'text'}
                autoComplete="on"
                placeholder={'Add Primary Contact phone'}
                defaultValue={''}
              />
            </FormControl>
          </div>
          <div>{t('ADD_CLINIC.PRIMARY_CONTACT_EMAIL')}</div>
          <FormControl fullWidth={true} variant="outlined">
            <Controller
              id="primary_contact_email"
              as={StyledOutlinedInput}
              margin={'dense'}
              control={control}
              name={'primary_contact_email'}
              type={'text'}
              value={'email'}
              autoComplete="on"
              placeholder={'Add Primary Contact Email'}
              defaultValue={''}
            />
          </FormControl>
          <div className={classes.addressText}>
            {t('ADD_CLINIC.ADMIN_DETAILS')}
          </div>
          <div className={classes.dividerView} />
          <div>{t('ADD_CLINIC.FIRST_NAME')}*</div>
          <FormControl
            error={!!errors.admin_first_name}
            fullWidth={true}
            variant="outlined">
            <Controller
              id="admin_first_name"
              as={StyledOutlinedInput}
              margin={'dense'}
              control={control}
              name={'admin_first_name'}
              type={'text'}
              value={'email'}
              autoComplete="on"
              placeholder={'Add admin first Name'}
              defaultValue=""
              error={!!errors.admin_first_name}
            />
            {errors.admin_first_name && (
              <FormHelperText>{t(errors.admin_first_name)}</FormHelperText>
            )}
          </FormControl>
          <div>{t('ADD_CLINIC.LAST_NAME')}*</div>
          <FormControl
            error={!!errors.admin_last_name}
            fullWidth={true}
            variant="outlined">
            <Controller
              id="admin_last_name"
              as={StyledOutlinedInput}
              margin={'dense'}
              control={control}
              name={'admin_last_name'}
              type={'text'}
              value={'email'}
              autoComplete="on"
              placeholder={'Add admin first Name'}
              defaultValue=""
              error={!!errors.admin_last_name}
            />
            {errors.admin_last_name && (
              <FormHelperText>{t(errors.admin_last_name)}</FormHelperText>
            )}
          </FormControl>
          <div>{t('ADD_CLINIC.EMAIL')}*</div>
          <FormControl
            error={!!errors.admin_email}
            fullWidth={true}
            variant="outlined">
            <Controller
              id="admin_email"
              as={StyledOutlinedInput}
              margin={'dense'}
              control={control}
              name={'admin_email'}
              type={'text'}
              value={'text'}
              autoComplete="on"
              placeholder={'Add admin email'}
              defaultValue=""
              error={!!errors.admin_email}
            />
            {errors.admin_email && (
              <FormHelperText>{t(errors.admin_email)}</FormHelperText>
            )}
          </FormControl>
          <div>{t('ADD_CLINIC.PHONE')}</div>
          <FormControl
            fullWidth={true}
            variant="outlined"
            style={{paddingBottom: 0}}>
            <Controller
              id="admin_phone"
              as={
                <PhoneInputField
                  value={''}
                  inputProps={{
                    name: 'admin_phone',
                    id: 'admin_phone',
                    required: false
                  }}
                  onFocus={(value: any, country: CountryData) =>
                    handleOnChange(value, country, 'admin_phone')
                  }
                />
              }
              margin={'dense'}
              control={control}
              name={'admin_phone'}
              type={'text'}
              value={'text'}
              autoComplete="on"
              placeholder={'Add admin Phone'}
              defaultValue=""
            />
          </FormControl>

          {/* patient shared section start */}
          {/* <div className={classes.switch}> */}
          <div
            style={{
              paddingTop: '0.3rem',
              paddingBottom: '0.8rem',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}>
            <div> {t('CLINICS.SWITCH_TEXT')}</div>
            <div style={{paddingLeft: '1rem'}}>
              {t('CLINICS.USER_DETAILS_TABLE.NO')}
              <Switch
                checked={sharedFlag}
                onChange={handleChange}
                color="primary"
                name="checkedB"
                disabled={false}
                inputProps={{'aria-label': 'primary checkbox'}}
              />
              {t('CLINICS.USER_DETAILS_TABLE.YES')}
            </div>
          </div>
          {sharedFlag && (
            <div className="emrprovider">
              {/* <div className={classes.emrProvider}> */}
              <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
                {t('ADD_CLINIC.EMRPROVIDER')}*
              </div>
              <FormControl fullWidth={true} variant="outlined">
                <Controller
                  id="emrprovider"
                  control={control}
                  name={'emrprovider'}
                  render={({onBlur}): React.ReactElement => (
                    <Select
                      id="designation-selector"
                      value={EMRProvider.label}
                      placeholder={t('FORM.PLACEHOLDERS.EMRPROVIDERS')}
                      onBlur={onBlur}
                      fullWidth={true}
                      variant="outlined"
                      margin="dense"
                      disabled={false}
                      IconComponent={KeyboardArrowDownIcon}
                      input={<CustomSelectField />}
                      style={{
                        height: '2.3rem',
                        borderRadius: '4px'
                      }}>
                      {EMRProvidersList.map((item: any, index: any) => (
                        <MenuItem
                          onClick={() => handleEMRProvider(item.id, item.name)}
                          value={item.id}
                          key={index}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </div>
          )}
          {/* patient shared section end */}

          <div className={classes.addressText}>
            {t('ADD_CLINIC.ADDRESS_TITLE')}
          </div>
          <div className={classes.dividerView} />
          <div>{t('ADD_CLINIC.ADDRESS')}</div>
          <FormControl fullWidth={true} variant="outlined">
            <Controller
              id="address_line"
              as={StyledOutlinedInput}
              margin={'dense'}
              control={control}
              name={'address_line'}
              type={'text'}
              value={'text'}
              autoComplete="on"
              placeholder={'Add Address Line'}
              defaultValue={''}
            />
          </FormControl>
          <div>{t('ADD_CLINIC.ADDRESS_LINE_2')}</div>
          <FormControl fullWidth={true} variant="outlined">
            <Controller
              id="address_line_2"
              as={StyledOutlinedInput}
              margin={'dense'}
              control={control}
              name={'address_line_2'}
              type={'text'}
              value={'text'}
              autoComplete="on"
              placeholder={'Add Address Line 2(optional)'}
              defaultValue={''}
            />
          </FormControl>
          <div className="addressMainView">
            <div className="cityView">
              <div>{t('ADD_CLINIC.CITY')}</div>
              <FormControl fullWidth={true} variant="outlined">
                <Controller
                  id="city"
                  as={StyledOutlinedInput}
                  margin={'dense'}
                  control={control}
                  name={'city'}
                  type={'text'}
                  value={'text'}
                  autoComplete="on"
                  placeholder={'Add City'}
                  defaultValue={''}
                />
              </FormControl>
            </div>
            <div className="stateView">
              <div className="paddingView">{t('ADD_CLINIC.STATE')}*</div>
              <FormControl
                error={!!errors.state}
                fullWidth={true}
                variant="outlined">
                <Controller
                  id="state"
                  render={({onBlur}): React.ReactElement => (
                    <Select
                      id="designation-selector"
                      value={state}
                      onChange={handleStateChange}
                      onBlur={onBlur}
                      fullWidth={true}
                      variant="outlined"
                      margin="dense"
                      input={<CustomSelectField />}
                      IconComponent={KeyboardArrowDownIcon}
                      className={[classes.input].join(' ')}
                      style={
                        errors.state
                          ? stateErrorStyles
                          : state === t('FORM.PLACEHOLDERS.STATE')
                          ? stateWithOpacity
                          : stateStyles
                      }>
                      {statesList.map(
                        (
                          item: {
                            name: string | number | string[] | undefined;
                            label: React.ReactNode;
                          },
                          index: any
                        ) => (
                          <MenuItem value={item.name} key={index}>
                            {item.name}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  )}
                  control={control}
                  name={'state'}
                  error={!!errors.state}
                />
                {errors.state && (
                  <FormHelperText>{t(errors.state)}</FormHelperText>
                )}
              </FormControl>
            </div>
            <div className="zipView">
              <div>{t('ADD_CLINIC.ZIP')}</div>
              <FormControl fullWidth={true} variant="outlined">
                <Controller
                  id="zip_postal_code"
                  as={StyledOutlinedInput}
                  margin={'dense'}
                  control={control}
                  name={'zip_postal_code'}
                  type={'number'}
                  value={'text'}
                  autoComplete="on"
                  onInput={(e: any) => {
                    e.target.value = validateZipCode(e);
                  }}
                  placeholder={'Add Zip/Postal Code'}
                  defaultValue={''}
                />
              </FormControl>
            </div>
          </div>
        </div>
        <div className="register-form-footer">
          <div className={classes.registerButton}>
            <Button
              variant="contained"
              size={'medium'}
              color="primary"
              className={['block', classes.button].join(' ')}
              type={'submit'}
              onClick={handleSubmit(handleFormSubmit, checkFormState)}
              disabled={buttonsDisable}>
              {t('REGISTER.REGISTER_NOW')}
            </Button>
          </div>
          <Link
            type={'button'}
            className={classes.login}
            component={'button'}
            onClick={redirectToLogin}>
            {t('REGISTER.REDIRECT_TO_LOGIN_PAGE')}
          </Link>
        </div>

        {/* New  */}
      </div>
    </div>
  );
};
export default Register;
